/**
 *  @swagger
 *  definitions:
 *    CrewmemberParams:
 *      type: object
 *      properties:
 *        Name:
 *          description: Name of the Crewmember
 *          type: string
 *        Birthdate:
 *          description: Birthdate of the Crewmember
 *          type: string
 *          format: date
 *        Email:
 *          description: Email of the Crewmember
 *          type: string
 *        Phone:
 *          description: Phone of the Crewmember
 *          type: string
 *        Gender:
 *          description: Gender of the Crewmember
 *          $ref: '#/components/enums/CrewmemberGender'
 *        Tshirt:
 *          description: Tshirt of the Crewmember
 *          $ref: '#/components/enums/CrewmemberTshirt'
 *        Qualification:
 *          description: Qualification of the Crewmember
 *          type: string
 *        Weight:
 *          description: Weight of the Crewmember
 *          type: integer
 *        Membership:
 *          description: Membership of the Crewmember
 *          type: string
 *        Language:
 *          description: Language of the Crewmember
 *          $ref: '#/components/enums/CrewmemberLanguage'
 *        RemoteImage:
 *          description: Rrmote Image of the Crewmember
 *          type: string
 *          nullable: true
 *
 *  @swagger
 *  definitions:
 *    CrewmemberCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberParams'
 *        - type: object
 *          required:
 *            - Name
 *            - Email
 *            - Language
 *
 *  @swagger
 *  definitions:
 *    CrewmemberQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Crewmember
 *              type: string
 *        - $ref: '#/definitions/CrewmemberParams'
 *
 *  @swagger
 *  definitions:
 *    CrewmemberUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Crewmember:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberCreateParams'
 *        - $ref: '#/definitions/CrewmemberUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Crewmember:
 *        allOf:
 *          - $ref: '#/definitions/Crewmember'
 *          - type: object
 *            properties:
 *              CrewmemberSocialUser:
 *                description: CrewmemberSocialUser of the Crewmember
 *                $ref: '#/components/schemas/CrewmemberSocialUser'
 *              CrewmemberAccessTokens:
 *                description: CrewmemberAccessTokens of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/CrewmemberAccessToken'
 *              CrewmemberPushTokens:
 *                description: CrewmemberPushTokens of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/CrewmemberPushToken'
 *              RegattaEntryCrewmembers:
 *                description: RegattaEntryCrewmembers of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntryCrewmember'
 *              Advertisements:
 *                description: Advertisements of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Advertisement'
 *              RegattaNotifications:
 *                description: RegattaNotifications of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaNotification'
 *              ChampionshipSubscriptions:
 *                description: ChampionshipSubscriptions of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipSubscription'
 *              RegattaSubscriptions:
 *                description: RegattaSubscriptions of the Crewmember
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaSubscription'
 */

// Enums
import {
  CrewmemberGender,
  CrewmemberLanguage,
  CrewmemberTshirt,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $Params = {
  Birthdate: null | string;
  Email: string;
  Gender?: CrewmemberGender | null;
  Language: CrewmemberLanguage;
  Membership: null | string;
  Name: string;
  Phone: null | string;
  Qualification: null | string;
  RemoteImage: null | string;
  Tshirt?: CrewmemberTshirt | null;
  Weight: null | number;
};

export type $CreateParams = $Params;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$Params>;

export type $Associations = {
  Advertisements?: Array<$Model<'Advertisement'>>;
  ChampionshipSubscriptions?: Array<$Model<'ChampionshipSubscription'>>;
  CrewmemberAccessTokens?: Array<$Model<'CrewmemberAccessToken'>>;
  CrewmemberPushTokens?: Array<$Model<'CrewmemberPushToken'>>;
  CrewmemberSocialUser?: $Model<'CrewmemberSocialUser'>;
  RegattaEntryCrewmembers?: Array<$Model<'RegattaEntryCrewmember'>>;
  RegattaNotifications?: Array<$Model<'RegattaNotification'>>;
  RegattaSubscriptions?: Array<$Model<'RegattaSubscription'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Birthdate: {
    allowNull: true,
    type: Sequelize.DATEONLY,
    validate: {
      notEmpty: true,
    },
  },
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    unique: true,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  Gender: {
    allowNull: true,
    type: Sequelize.ENUM(...Object.values(CrewmemberGender)),
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Language: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(CrewmemberLanguage)),
    validate: {
      notEmpty: true,
    },
  },
  Membership: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Phone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Qualification: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RemoteImage: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Tshirt: {
    allowNull: true,
    type: Sequelize.ENUM(...Object.values(CrewmemberTshirt)),
    validate: {
      notEmpty: true,
    },
  },
  Weight: {
    allowNull: true,
    type: Sequelize.INTEGER.UNSIGNED,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Crewmember'> => {
  const Crewmember = stack.define(
    'Crewmember',
    fields(Sequelize),
    {
      tableName: 'Crewmembers',
    },
  );

  Crewmember.associate = (models: $ModelClasses) => {
    models.Crewmember.hasMany(
      models.CrewmemberSocialUser,
      {
        as: 'CrewmemberSocialUsers',
        foreignKey: 'Email',
        sourceKey: 'Email',
      },
    );
    models.Crewmember.hasMany(
      models.CrewmemberAccessToken,
      {
        as: 'CrewmemberAccessTokens',
        foreignKey: 'CrewmemberId',
      },
    );
    models.Crewmember.hasMany(
      models.CrewmemberPushToken,
      {
        as: 'CrewmemberPushTokens',
        foreignKey: 'CrewmemberId',
      },
    );
    models.Crewmember.hasMany(
      models.RegattaEntryCrewmember,
      {
        as: 'RegattaEntryCrewmembers',
        foreignKey: 'Email',
        sourceKey: 'Email',
      },
    );
    models.Crewmember.hasMany(
      models.Advertisement,
      {
        as: 'Advertisements',
        foreignKey: 'Email',
      },
    );
    models.Crewmember.hasMany(
      models.ChampionshipSubscription,
      {
        as: 'ChampionshipSubscriptions',
        foreignKey: 'CrewmemberId',
      },
    );
    models.Crewmember.hasMany(
      models.RegattaSubscription,
      {
        as: 'RegattaSubscriptions',
        foreignKey: 'CrewmemberId',
      },
    );
    models.Crewmember.belongsToMany(
      models.RegattaNotification,
      {
        as: 'RegattaNotifications',
        foreignKey: 'CrewmemberId',
        otherKey: 'RegattaNotificationId',
        through: models.CrewmemberRegattaNotification,
      },
    );
  };

  Crewmember.addScope(
    'defaultScope',
    {
      order: [['Name', 'ASC']],
    },
  );

  return Crewmember;
});
